/**
 * 🦸 Hero Skill Bar — Feb 2026 (Phase 1A)
 *
 * Wraps the existing Move Picker with the player's HERO CLASS identity:
 *   • Class portrait + name banner
 *   • Animated Mana Bubble bar (0 → 100)
 *   • 5 active-skill buttons + 2 ultimate slots
 *   • Locked skills show 🔒 with "Unlock @ Lv X" hint (greyed)
 *
 * Phase 1A scope: when the player clicks a skill, we route it through the
 * existing /arena/turn `move=heavy|light` API based on the skill's `kind`:
 *   • generator    → light  (cheap, 0 cost, builds mana)
 *   • single/aoe   → heavy  (heavier hit, narrative-only for now)
 *   • shield/ult   → heavy  (placeholder until Phase 2 effect engine ships)
 *
 * In Phase 2, the backend will receive the actual `skill_id` and execute the
 * `effects[]` array. The UI shape stays identical.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { playSfx } from "@/utils/battleSfx";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CLASS_BADGE = {
  ash_rogue: { icon: "🗡️", name: "Ash Rogue",        color: "#A855F7", bg: "from-purple-900/60 to-black/60" },
  warrior:   { icon: "🛡️", name: "Volcanic Warrior", color: "#DC2626", bg: "from-red-900/60 to-orange-950/60" },
  mage:      { icon: "🔮", name: "Phoenix Mage",     color: "#F97316", bg: "from-orange-900/60 to-rose-950/60" },
  archer:    { icon: "🏹", name: "Solar Archer",     color: "#F59E0B", bg: "from-amber-900/60 to-yellow-950/60" },
};

/** Map a skill kind to the legacy backend move enum (Phase 1A bridge) */
function skillToMove(skill) {
  if (!skill) return "light";
  if (skill.kind === "generator") return "light";
  return "heavy";  // every non-generator routes as heavy for now
}

export default function HeroSkillBar({ onPick, onSkill, fightId, serverMana, serverManaCap, disabled }) {
  const [kit, setKit] = useState(null);
  const [petLevel, setPetLevel] = useState(1);
  const [points, setPoints] = useState(0);
  const [localMana, setLocalMana] = useState(0);
  const [casting, setCasting] = useState(null);

  // 🦸 Phase 2: prefer server-side mana when available; fall back to local optimistic state
  const useServerMana = typeof serverMana === "number";
  const mana = useServerMana ? serverMana : localMana;
  const setMana = useServerMana ? (() => {}) : setLocalMana;

  // Load the player's annotated skill kit on mount
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await axios.get(`${API}/hero-class/skills`);
        if (!alive) return;
        setKit(r.data?.kit);
        setPetLevel(r.data?.pet_level || 1);
        setPoints(r.data?.chronograph_points || 0);
      } catch {
        // No class selected yet or endpoint error — bar will render empty
      }
    })();
    return () => { alive = false; };
  }, [fightId]);

  if (!kit) return null;

  const resource = kit.resource || { name: "Mana", cap: 100, icon: "💠" };
  const manaCap = (useServerMana && serverManaCap) ? serverManaCap : (resource.cap || 100);
  const manaPct = Math.min(100, Math.round((mana / manaCap) * 100));

  const canCast = (skill) => skill.unlocked && mana >= (skill.cost || 0) && !disabled && !casting;

  const handleClick = async (skill) => {
    if (!canCast(skill)) {
      if (!skill.unlocked) {
        playSfx("defend");  // soft "no" buzz
      } else if (mana < (skill.cost || 0)) {
        playSfx("defend");
      }
      return;
    }
    setCasting(skill.id);
    // 🦸⚔️ Phase 2 — if onSkill is wired (live fight) → call real backend skill executor.
    // Otherwise fall back to legacy /arena/turn move shim.
    try {
      if (onSkill && fightId) {
        const res = await onSkill(skill);
        // Server-side mana is now authoritative; no local optimistic update needed
        if (!res && !useServerMana) {
          // failed call — refund optimistic mana if we were tracking locally
          if (skill.kind === "generator") {
            setLocalMana((m) => Math.max(0, m - (skill.gain || 0)));
          } else {
            setLocalMana((m) => Math.min(manaCap, m + (skill.cost || 0)));
          }
        } else if (!useServerMana) {
          // No server-tracked mana → local optimistic update
          if (skill.kind === "generator") {
            setLocalMana((m) => Math.min(manaCap, m + (skill.gain || 0)));
          } else {
            setLocalMana((m) => Math.max(0, m - (skill.cost || 0)));
          }
        }
      } else {
        // Legacy fallback path — local mana + /arena/turn heavy/light routing
        if (skill.kind === "generator") {
          setLocalMana((m) => Math.min(manaCap, m + (skill.gain || 0)));
        } else {
          setLocalMana((m) => Math.max(0, m - (skill.cost || 0)));
        }
        await onPick(skillToMove(skill));
      }
    } finally {
      setTimeout(() => setCasting(null), 350);
    }
  };

  const badge = CLASS_BADGE[kit.class_id] || CLASS_BADGE.ash_rogue;

  return (
    <div
      className={`bg-gradient-to-br ${badge.bg} border-t-2 border-b-2 p-3 sm:p-4`}
      style={{ borderColor: `${badge.color}66` }}
      data-testid="hero-skill-bar"
    >
      {/* Class header + Mana bubble */}
      <div className="flex items-center gap-3 mb-2">
        <div className="text-2xl sm:text-3xl drop-shadow-[0_0_8px_currentColor]" style={{ color: badge.color }}>
          {badge.icon}
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-[0.3em] text-white/60 font-bold">Hero Class · Lv {petLevel}</div>
          <div className="text-base sm:text-lg font-black text-white truncate" style={{ fontFamily: "Anton, sans-serif" }}>
            {badge.name.toUpperCase()}
          </div>
        </div>
        {/* Chronograph Points + Mana orb counter */}
        <div className="text-right">
          <div className="text-[10px] uppercase tracking-wider text-white/60 font-bold">{resource.name}</div>
          <div className="text-lg sm:text-xl font-black tabular-nums" style={{ color: badge.color }} data-testid="hero-mana-counter">
            {mana}/{manaCap}
          </div>
          <div className="text-[9px] uppercase tracking-wider text-amber-300/80 font-bold mt-0.5" title="Chronograph Points · Train with Roc at the Gym">
            🌳 {points} CP
          </div>
        </div>
      </div>

      {/* Mana Bubble bar — animated fill */}
      <div className="relative h-3 bg-black/60 rounded-full overflow-hidden border border-white/10 mb-3">
        <div
          className="h-full transition-all duration-500 ease-out rounded-full"
          style={{
            width: `${manaPct}%`,
            background: `linear-gradient(90deg, ${badge.color}55, ${badge.color}, ${badge.color}cc)`,
            boxShadow: `0 0 10px ${badge.color}, inset 0 0 6px ${badge.color}`,
          }}
        />
        {/* Bubble shimmer */}
        <div className="absolute inset-0 pointer-events-none" style={{
          background: "linear-gradient(180deg, rgba(255,255,255,0.18) 0%, transparent 50%)",
        }} />
      </div>

      {/* Active skills row */}
      <div className="grid grid-cols-5 gap-1.5 sm:gap-2 mb-2">
        {kit.actives.map((sk) => (
          <SkillButton
            key={sk.id}
            skill={sk}
            mana={mana}
            color={badge.color}
            casting={casting === sk.id}
            onClick={() => handleClick(sk)}
          />
        ))}
      </div>

      {/* Ultimates row — wider buttons since there are only 2 */}
      <div className="grid grid-cols-2 gap-1.5 sm:gap-2">
        {kit.ultimates.map((sk) => (
          <SkillButton
            key={sk.id}
            skill={sk}
            mana={mana}
            color="#DC2626"
            isUlt
            casting={casting === sk.id}
            onClick={() => handleClick(sk)}
          />
        ))}
      </div>

      {/* Phase 1A note — replace with proper trainer UI when Roc's Gym lands */}
      <div className="mt-2 text-[9px] uppercase tracking-widest text-white/40 text-center">
        🌳 Train with Roc at the Gym to earn Chronograph Points · Phase 1A preview
      </div>
    </div>
  );
}

function SkillButton({ skill, mana, color, isUlt, casting, onClick }) {
  const locked = !skill.unlocked;
  const tooPoor = !locked && mana < (skill.cost || 0);
  const disabled = locked || tooPoor;
  const tag = isUlt ? "ULT" : skill.kind === "generator" ? "GEN" : skill.kind.toUpperCase();
  const costLabel = skill.kind === "generator"
    ? `+${skill.gain}`
    : skill.cost;

  // Hint copy — points to Roc at the Gym for locked skills
  const titleHint = locked
    ? `🔒 ${skill.name} — Spend ${skill.chrono_cost} 🌳 Chronograph Points at the Gym (Train with Roc)`
    : `${skill.tagline || skill.name} — ${skill.long_desc || ""}`;

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      data-testid={`hero-skill-${skill.id}${disabled ? "-locked" : ""}`}
      className={`relative flex flex-col items-center justify-center px-1 py-2 sm:py-2.5 border-2 transition-all rounded-md ${
        disabled
          ? "bg-black/60 border-white/10 text-white/40 cursor-not-allowed"
          : `bg-black/40 hover:bg-black/20 border-white/40 hover:border-white text-white ${casting ? "scale-95 brightness-150" : "hover:scale-105"}`
      } ${isUlt ? "min-h-[60px]" : "min-h-[52px]"}`}
      style={!disabled ? { borderColor: `${color}aa`, boxShadow: `0 0 10px ${color}33` } : undefined}
      title={titleHint}
    >
      <div className="text-base sm:text-lg leading-none">
        {locked ? "🔒" : skill.icon}
      </div>
      <div className="text-[8px] sm:text-[9px] uppercase tracking-wider font-bold truncate w-full text-center">
        {locked ? `🌳 ${skill.chrono_cost} CP` : skill.name}
      </div>
      <div className={`text-[9px] font-black tabular-nums ${
        skill.kind === "generator" ? "text-emerald-400" : "text-cyan-300"
      } ${locked ? "opacity-40" : ""}`}>
        {locked ? "—" : costLabel}
      </div>
      <div className="absolute top-0 right-0.5 text-[7px] font-black tracking-wider text-white/40">
        {tag}
      </div>
    </button>
  );
}
